import { z } from 'zod';
import { EquipmentOwnershipSchema, EquipmentUnitSchema, LibraryImportStatusSchema, LibraryImportTargetSchema, LibraryItemTypeSchema, WageRateSetKindSchema, } from '../lib/enums';
import { zIsoDateTime, zJurisdiction, zQtyNonNeg, zRateNonNeg, zUuid, } from './common';
/**
 * Cost libraries — the contractor's OWN costs. Nothing here is ever seeded
 * with market prices; `unitCost`/`baseRate` are nullable and stay null until
 * the contractor fills them.
 *
 * Versioning doctrine is snapshot-on-use: estimate lines copy resolved values
 * at insert time; library edits never retroactively change estimates.
 */
// ---------------------------------------------------------------- labor roles
/** One burden component. Percent applies to base rate; per_hour adds dollars/hour. */
export const BurdenComponentSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('percent'),
        label: z.string().min(1).max(120),
        /** Percent of base rate, e.g. 7.65 for FICA. */
        value: z.number().finite().min(0).max(1000),
    })
        .strict(),
    z
        .object({
        kind: z.literal('per_hour'),
        label: z.string().min(1).max(120),
        /** Dollars per hour added on top of base. */
        value: z.number().finite().min(0),
    })
        .strict(),
]);
export const LaborRoleSchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(200),
    baseRate: zRateNonNeg.nullable(),
    burden: z.array(BurdenComponentSchema),
    /**
     * Derived by the shared engine: base + Σ(base×pct) + Σ(per_hour).
     * Null while baseRate is null. Never stored — always recomputed.
     */
    burdenedRate: zRateNonNeg.nullable(),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateLaborRoleRequestSchema = z
    .object({
    name: z.string().min(1).max(200),
    baseRate: zRateNonNeg.nullable().optional(),
    burden: z.array(BurdenComponentSchema).max(30).optional(),
})
    .strict();
export const UpdateLaborRoleRequestSchema = CreateLaborRoleRequestSchema.partial();
// ---------------------------------------------------------------------- crews
export const CrewMemberSchema = z.object({
    laborRoleId: zUuid,
    quantity: z.number().int().min(1).max(200),
});
export const CrewSchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(200),
    members: z.array(CrewMemberSchema),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateCrewRequestSchema = z
    .object({
    name: z.string().min(1).max(200),
    members: z.array(CrewMemberSchema.extend({}).strict()).min(1).max(50),
})
    .strict();
export const UpdateCrewRequestSchema = CreateCrewRequestSchema.partial();
// ------------------------------------------------------------- wage rate sets
export const WageRateSchema = z.object({
    id: zUuid,
    classification: z.string().min(1).max(200),
    baseRate: zRateNonNeg,
    fringeRate: zRateNonNeg,
});
export const WageRateSetSchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(200),
    jurisdiction: zJurisdiction.nullable(),
    kind: WageRateSetKindSchema,
    rates: z.array(WageRateSchema),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
const WageRateWriteSchema = z
    .object({
    classification: z.string().min(1).max(200),
    baseRate: zRateNonNeg,
    fringeRate: zRateNonNeg,
})
    .strict();
export const CreateWageRateSetRequestSchema = z
    .object({
    name: z.string().min(1).max(200),
    jurisdiction: zJurisdiction.nullable().optional(),
    kind: WageRateSetKindSchema,
    rates: z.array(WageRateWriteSchema).max(200).optional(),
})
    .strict();
export const UpdateWageRateSetRequestSchema = CreateWageRateSetRequestSchema.partial();
// ------------------------------------------------------------------ materials
export const MaterialSchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(300),
    unit: z.string().min(1).max(20),
    unitCost: zRateNonNeg.nullable(),
    category: z.string().max(120).nullable(),
    supplier: z.string().max(200).nullable(),
    tradeTags: z.array(z.string().min(1).max(64)),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateMaterialRequestSchema = z
    .object({
    name: z.string().min(1).max(300),
    unit: z.string().min(1).max(20),
    unitCost: zRateNonNeg.nullable().optional(),
    category: z.string().max(120).nullable().optional(),
    supplier: z.string().max(200).nullable().optional(),
    tradeTags: z.array(z.string().min(1).max(64)).max(20).optional(),
})
    .strict();
export const UpdateMaterialRequestSchema = CreateMaterialRequestSchema.partial();
// ------------------------------------------------------------------ equipment
export const EquipmentRateSchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(300),
    unit: EquipmentUnitSchema,
    rate: zRateNonNeg.nullable(),
    ownership: EquipmentOwnershipSchema,
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateEquipmentRateRequestSchema = z
    .object({
    name: z.string().min(1).max(300),
    unit: EquipmentUnitSchema,
    rate: zRateNonNeg.nullable().optional(),
    ownership: EquipmentOwnershipSchema,
})
    .strict();
export const UpdateEquipmentRateRequestSchema = CreateEquipmentRateRequestSchema.partial();
// ----------------------------------------------------------------- assemblies
export const AssemblyComponentSchema = z.discriminatedUnion('kind', [
    z
        .object({
        kind: z.literal('labor'),
        laborRoleId: zUuid,
        /** Labor hours per assembly unit. */
        hoursPerUnit: zQtyNonNeg,
    })
        .strict(),
    z
        .object({
        kind: z.literal('material'),
        materialId: zUuid,
        qtyPerUnit: zQtyNonNeg,
        /** Waste percent (0–100) applied to material quantity. */
        wastePct: z.number().finite().min(0).max(100),
    })
        .strict(),
    z
        .object({
        kind: z.literal('equipment'),
        equipmentRateId: zUuid,
        qtyPerUnit: zQtyNonNeg,
    })
        .strict(),
]);
export const AssemblySchema = z.object({
    id: zUuid,
    name: z.string().min(1).max(300),
    unit: z.string().min(1).max(20),
    tradeProfileKey: z.string().max(64).nullable(),
    description: z.string().max(2000).nullable(),
    components: z.array(AssemblyComponentSchema),
    createdAt: zIsoDateTime,
    updatedAt: zIsoDateTime,
});
export const CreateAssemblyRequestSchema = z
    .object({
    name: z.string().min(1).max(300),
    unit: z.string().min(1).max(20),
    tradeProfileKey: z.string().max(64).nullable().optional(),
    description: z.string().max(2000).nullable().optional(),
    components: z.array(AssemblyComponentSchema).max(60).optional(),
})
    .strict();
export const UpdateAssemblyRequestSchema = CreateAssemblyRequestSchema.partial();
// ----------------------------------------------------------------- CSV import
export const LibraryImportSchema = z.object({
    id: zUuid,
    targetType: LibraryImportTargetSchema,
    status: LibraryImportStatusSchema,
    filename: z.string().min(1).max(300),
    /** Column headers found in the uploaded CSV. */
    sourceColumns: z.array(z.string()),
    /** First rows for mapping preview. */
    sampleRows: z.array(z.record(z.string())),
    /** targetField → source column name (null = not mapped). */
    mapping: z.record(z.string().nullable()).nullable(),
    rowErrors: z.array(z.object({
        row: z.number().int().min(1),
        message: z.string(),
    })),
    createdAt: zIsoDateTime,
});
/** Multipart metadata accompanying the CSV upload. */
export const CreateLibraryImportRequestSchema = z.object({
    targetType: LibraryImportTargetSchema,
});
export const UpdateImportMappingRequestSchema = z
    .object({
    mapping: z.record(z.string().nullable()),
})
    .strict();
export const CommitImportResponseSchema = z.object({
    id: zUuid,
    status: LibraryImportStatusSchema,
    insertedCount: z.number().int().min(0),
    rowErrors: z.array(z.object({
        row: z.number().int().min(1),
        message: z.string(),
    })),
});
// ---------------------------------------------------- copy-from-estimate etc.
export const CopyFromEstimateRequestSchema = z
    .object({
    estimateId: zUuid,
    /** Which line kinds to harvest back into libraries; default all copyable. */
    include: z.array(z.enum(['labor', 'material', 'equipment'])).min(1).optional(),
})
    .strict();
export const CopyFromEstimateResponseSchema = z.object({
    createdCounts: z.object({
        laborRoles: z.number().int().min(0),
        materials: z.number().int().min(0),
        equipmentRates: z.number().int().min(0),
    }),
});
/** Append-only change history row for any library item. */
export const LibraryItemRevisionSchema = z.object({
    id: zUuid,
    itemType: LibraryItemTypeSchema,
    itemId: zUuid,
    before: z.record(z.unknown()).nullable(),
    after: z.record(z.unknown()),
    changedByUserId: zUuid.nullable(),
    createdAt: zIsoDateTime,
});
/** Ref shape used by pickers/type-ahead: one library item of any kind. */
export const LibraryPickerItemSchema = z.object({
    itemType: z.enum(['labor_role', 'crew', 'material', 'equipment_rate', 'assembly']),
    id: zUuid,
    name: z.string(),
    unit: z.string().nullable(),
    /** Resolved unit cost when priceable; null when the item is unpriced. */
    unitCost: zRateNonNeg.nullable(),
});
//# sourceMappingURL=library.js.map